import {I18nKeys} from '@multipass-i18n';
import {Button, Checkbox, FieldHelp, FieldLabel, Notice, TextInput} from '@multipass-ui-components';
import {syncModelWithSelectorOptions, usePublishEvent, useSyncModelWithSelector} from 'esp-js-react';
import {Mail} from 'lucide-react';
import * as React from 'react';

import {
    SESSION_STORE_ID,
    SessionEvents,
    SessionStoreModel,
    SignUpRequestedEvent,
    useT,
    useTReason,
} from '../../logic';
import {AuthHead, AuthLayout, AuthSwitchRow} from '../auth/AuthLayout';
import {EnterCodeView} from '../auth/EnterCodeView';
import {RouterLink} from '../shell/RouterLink';

const SignupForm: React.FunctionComponent<{session: SessionStoreModel}> = ({session}: {session: SessionStoreModel}) => {
    const [displayName, setDisplayName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [agreed, setAgreed] = React.useState(false);
    const publishEvent = usePublishEvent();
    const t = useT();
    const tReason = useTReason();
    const documentsLoaded = session.legalDocumentsLoaded;
    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const trimmedEmail = email.trim();
        const trimmedDisplayName = displayName.trim();
        if (!trimmedEmail || !trimmedDisplayName || !documentsLoaded || !agreed) {
            return;
        }
        publishEvent(SESSION_STORE_ID, SessionEvents.signUpRequested, {
            email: trimmedEmail,
            displayName: trimmedDisplayName,
            acceptedDocumentIds: session.allLegalDocumentIds,
        } satisfies SignUpRequestedEvent);
    };
    return (
        <>
            <AuthHead title={t(I18nKeys.signUp.title)} sub={t(I18nKeys.signUp.subtitle)}/>
            {session.formError?.kind === 'existingEmail' && (
                <Notice role="alert" className="mb-5">
                    {tReason(session.formError.code, session.formError.message)}{' '}
                    <RouterLink to="/login" className="font-semibold text-link no-underline">{t(I18nKeys.signUp.switchLink)}</RouterLink>
                </Notice>
            )}
            <form onSubmit={onSubmit}>
                <div className="mb-5">
                    <FieldLabel htmlFor="signup-display-name">{t(I18nKeys.signUp.displayNameLabel)}</FieldLabel>
                    <TextInput
                        id="signup-display-name"
                        type="text"
                        autoComplete="name"
                        placeholder={t(I18nKeys.signUp.displayNamePlaceholder)}
                        value={displayName}
                        onChange={e => setDisplayName(e.target.value)}
                        required
                    />
                </div>
                <div className="mb-5">
                    <FieldLabel htmlFor="signup-email">{t(I18nKeys.signUp.emailLabel)}</FieldLabel>
                    <TextInput
                        id="signup-email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                    />
                    <FieldHelp>{t(I18nKeys.signUp.emailHelp)}</FieldHelp>
                </div>
                <Button type="submit" size="lg" block disabled={!documentsLoaded || !agreed}>
                    <Mail size={20}/>
                    {t(I18nKeys.signUp.submit)}
                </Button>
                <Checkbox checked={agreed} onChange={setAgreed} className="mt-5">
                    {t(I18nKeys.signUp.consentLead, {appName: t(I18nKeys.common.appName)})}
                    <a href="/legal#terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-link">{t(I18nKeys.signUp.termsLink)}</a>
                    {t(I18nKeys.signUp.consentMid)}
                    <a href="/legal#privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-link">{t(I18nKeys.signUp.privacyLink)}</a>
                    {t(I18nKeys.signUp.consentTrail)}
                </Checkbox>
            </form>
            <AuthSwitchRow>
                {t(I18nKeys.signUp.switchPrompt)} <RouterLink to="/login" className="font-bold text-link no-underline">{t(I18nKeys.signUp.switchLink)}</RouterLink>
            </AuthSwitchRow>
        </>
    );
};

export const SignupPage: React.FunctionComponent = () => {
    const session = useSyncModelWithSelector<SessionStoreModel, SessionStoreModel>(
        m => m,
        syncModelWithSelectorOptions<SessionStoreModel>().setStoreId(SESSION_STORE_ID)
    );
    return (
        <AuthLayout>
            {session.pendingEmail !== null ? <EnterCodeView session={session}/> : <SignupForm session={session}/>}
        </AuthLayout>
    );
};
